import React from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Collapse,
    IconButton,
    IconButtonProps,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ICardPanel } from 'src/models/interfaces/ICardPanel';
import { useTranslation } from 'react-i18next';

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled(
    (props: ExpandMoreProps) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { expand, ...other } = props;
        return <IconButton {...other} />;
    }
)(
    ({ theme, expand }) => ({
        transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
        marginLeft: 'auto',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    })
);

/**
 * @function CardPannel
 * @description Elemento en el cual se va a renderizar el error.
 * @param {StatusTypes} messageType: indica el tipo de error "warning" | "error" | "info" | "success"
 * @param {string} title: Titulo pincipal que se va a visualizar
 * @param {string} subtitle: Subtitulo principal
 * @param {React.JSX.Element} messageCollapse: Detalle que se va a visualizar colapsado (opcional)
 * @param {React.JSX.Element} messageCardContent: Detalle que se va a visualizar en todo momento (opcional)
 * @returns {React.JSX.Element}
 */
function CardPannel({messageType, title, subtitle, dateTime, messageCollapse, messageCardContent, onDelete}:ICardPanel): React.JSX.Element {
    const [expanded, setExpanded] = React.useState(false);

	const { t } = useTranslation();

    const handleExpandClick = (event: React.SyntheticEvent) => {
        event.preventDefault();
        setExpanded(!expanded);
    };

    return (
        <Box sx={{my:"0.5rem"}}>
            <Card>
                <CardHeader
                    avatar = {
                        <>
                            {
                                messageType === "warning" ? <WarningAmberIcon color='warning' />
                                : messageType === "error" ? <ErrorOutlineOutlinedIcon color='error' />
                                : messageType === "info" ? <InfoOutlinedIcon color='info' />
                                :<CheckCircleOutlineIcon color='success' />
                            }
                        </>
                    }
                    action={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {messageCollapse && <ExpandMore
                                expand={expanded}
                                onClick={handleExpandClick}
                                aria-expanded={expanded}
                                aria-label={t("tooltip.showMore") as string}
                            >
                                <ExpandMoreIcon />
                            </ExpandMore>}
                            {onDelete && <IconButton
                                size="small"
                                aria-label="delete message"
                                onClick={onDelete}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>}
                        </Box>
                    }
                    title = {title}
                    subheader = {
                        <>
                            <span style={{ display: 'block' }}>{subtitle}</span>
                            {dateTime && (
                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary', marginTop: '2px' }}>
                                    {dateTime.toLocaleString()}
                                </span>
                            )}
                        </>
                    }
                    sx={{
                        userSelect: "all"
                    }}
                />
                {
                    messageCardContent && <CardContent sx={{userSelect: "all"}}>
                        <Typography variant="body2" color="text.secondary">
                            {messageCardContent}
                        </Typography>
                    </CardContent>
                }
                {
                    messageCollapse && <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <CardContent sx={{userSelect: "all"}}>
                            {messageCollapse}
                        </CardContent>
                    </Collapse>
                }
            </Card>
        </Box>
    )
}

export default CardPannel;
